import { FocusMonitor } from '@angular/cdk/a11y';
import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NgControl,
  Validators,
} from '@angular/forms';
import { MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';

import { Subject } from 'rxjs';

@Component({
  template: '',
  providers: [
    { provide: MatFormFieldControl, useExisting: BaseMatControlComponent },
  ],
})
export abstract class BaseMatControlComponent<T>
  implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<T>
{
  @Input() label = '';
  @Input() hideRequiredLabel = false;
  @Input() showErrorMessages = true;
  @HostBinding('class.readonly') @Input() readonly: true | null = null;

  static nextId = 0;

  controlIdentity!: string;
  controlType!: 'input' | 'textarea' | 'mat-select' | 'mat-checkbox'; // TODO extend

  id!: string;

  focused = false;
  errorState = false;
  autofilled = false;
  empty = false;
  userAriaDescribedBy?: string;
  stateChanges = new Subject<void>();
  private onTouchedCallback!: () => void;
  private onChangeCallback!: (_: any) => void;

  get formParent(): FormGroup<any> {
    return this.ngControl.control?.parent as FormGroup<any>;
  }

  get name(): string {
    return `${this.ngControl.name}`;
  }

  protected constructor(
    @Optional() @Self() public ngControl: NgControl,
    protected formBuilder: FormBuilder,
    protected focusMonitor: FocusMonitor,
    protected elRef: ElementRef
  ) {
    focusMonitor.monitor(elRef.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @HostBinding('class.floating') get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  // tslint:disable-next-line:variable-name
  private _placeholder = '';

  @Input() get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  get required(): boolean {
    return (
      !!this.ngControl.control?.hasValidator(Validators.required) ||
      !!this.ngControl.control?.hasValidator(Validators.requiredTrue)
    );
  }

  get disabled(): boolean {
    return !!this.ngControl.disabled;
  }

  // tslint:disable-next-line:variable-name
  private _value!: T;

  get value(): T {
    return this._value;
  }

  set value(v: T) {
    if (JSON.stringify(v) !== JSON.stringify(this._value)) {
      this._value = v;
      this.stateChanges.next();
      this.onChangeCallback(this._value);
    }
  }

  ngOnInit(): void {
    this.id = `${this.name}-${BaseMatControlComponent.nextId++}`;
    if (this.readonly) {
      this.elRef.nativeElement
        .closest('mat-form-field')
        .classList.add('readonly');
    }
  }

  public onBlur(): void {
    this.onTouchedCallback();
  }

  public writeValue(value: any): void {
    this._value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  public onContainerClick(event: MouseEvent): void {
    if (
      (event.target as Element).tagName.toLowerCase() !== this.controlType &&
      !this.readonly
    ) {
      this.elRef.nativeElement.querySelector(this.controlType).focus();
    }
  }

  public setDescribedByIds(ids: string[]): void {
    document
      .getElementById(this.id)
      ?.setAttribute('aria-describedby', ids.join(' '));
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elRef.nativeElement);
  }
}
